import DOMPurify from 'dompurify';

import { enforceLinkSafety } from '@/lib/email/linkSafety';

/**
 * Scrubs a stored signature block before it is injected into the live page.
 *
 * `SignatureElement` renders the opaque signature node with
 * `dangerouslySetInnerHTML`, so this is the client-side half of the app's
 * defence-in-depth for stored rich HTML. It deliberately mirrors the two
 * other stored-HTML render sites — `lib/email/EmailIframe.tsx` and
 * `lib/crm/record/notes/NoteCard.subcomponents.tsx` — both of which run
 * DOMPurify before injecting. The backend nh3 allowlist
 * (`apps/crm_events/services/event_normalizer.py`) already scrubs every CRM
 * property on write and remains the primary control; this does not replace
 * it. What it removes is the assumption that a single backend allowlist is
 * the *only* thing standing between a stored value and the DOM — an
 * assumption nothing in the code recorded.
 *
 * Sanitisation happens at **render** time only. The signature node's `html`
 * is carried verbatim through parse -> serialize, and that contract is what
 * fixes #10416; scrubbing into the stored value would reintroduce exactly
 * the silent data loss this editor exists to prevent. Because
 * `serializePlateToHtml` reads `node.html` rather than the rendered DOM,
 * nothing here can affect what is persisted.
 *
 * No allowlist is widened. DOMPurify's defaults already permit everything a
 * real signature needs: `<table>`/`<tbody>`/`<tr>`/`<td>`, inline `style`,
 * `data-*` attributes, and `https` image and link URLs. The only attribute
 * the defaults drop is the inert `src="about:blank"` placeholder that
 * `parse-html-to-plate-value.ts` substitutes for `file:ID` images — harmless,
 * because the file reference itself travels in `data-file-id` and an
 * `about:blank` image renders nothing either way.
 *
 * Link safety is applied for the same reason the email viewer applies it: the
 * block renders inside `contentEditable={false}`, so anchors within it are
 * genuinely clickable and carry the usual tabnabbing exposure.
 */
export function sanitizeSignatureHtml(html: string): string {
  if (!html) {
    return '';
  }
  return enforceLinkSafety(DOMPurify.sanitize(html));
}
